import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { pick, get } from 'lodash';
import {
  getFormValues,
  change,
  isPristine,
  reset,
  getFormSyncErrors,
} from 'redux-form';

import { Main, Filter } from 'Containers';
import { LoadingPage, YesNoDialog } from 'Components/';
import { CreateEditOrderEnum, ItemEnum } from 'Common/FieldEnum';
import { CREATE_EDIT_ORDER_FORM } from 'Common/FormEnum';
import { TableNamesEnum } from 'Common/Constants/Enumeration/TableEnum';
import { toastError } from 'Common/ToastHelper';
import { GridEnum } from 'Common/Constants/Enumeration/GridEnum';
import {
  DATE_FORMAT_FOR_UI,
  DATE_FORMAT_FOR_SERVER,
  UNSAVED_MESSAGE,
} from 'Common/Constants';
import {
  StatusOptionEnum,
  TypeOptionEnum,
  CreateEditDupOrderEnum,
  ManageProcessOrderStatusEnum,
} from 'Common/Constants/Enumeration/ManageProcessOrderEnum';

import { CreateEditOrderForm } from '../../components/createEditOrder/CreateEditOrderForm';
import * as CreateEditOrderSelectors from '../../state/createEditOrder/selectors';
import * as CreateEditOrderActions from '../../state/createEditOrder/actions';
import { makeOrderTypes } from '../../helper';
import { errorToast, successToast } from 'Common/ToastHelper';

class CreateEditOrderContainer extends Component {
  static path =
    '/manageprocessorder/:IsNewEditDup/:SiteId/:OrderId/:DeliveryDate';

  state = {
    parItems: null,
    showUnsavedModal: false,
    showDeliveryDateModal: false,
    showResetModal: false,
  };

  rootUrl = '/manageprocessorder';

  componentDidMount() {
    const {
      match: {
        params: { SiteId, OrderId, DeliveryDate },
      },
      getOrderDetails,
      getAllSiteItems,
      getOrderTypes,
    } = this.props;

    const orderIdInt = parseInt(OrderId, 10);

    //if we have an existing site id and delivery date, fetch site items
    if (parseInt(SiteId, 10) && parseInt(DeliveryDate, 10)) {
      getAllSiteItems(SiteId, DeliveryDate, orderIdInt ? OrderId : null);
    }

    //if we have an existing orderId, fetch order items
    if (orderIdInt) {
      getOrderDetails({ Id: OrderId, SiteId, DeliveryDate });
    }

    getOrderTypes();
  }

  componentDidUpdate(prevProps) {
    const {
      getParItemsResult,
      clearPostSaveOrderResult,
      history,
      getAllSiteItemsResult,
      formValues: { [CreateEditOrderEnum.TransactionDetail]: orderItems },
      change,
    } = this.props;

    //when we load par items, apply them to our order items
    if (
      getParItemsResult &&
      getParItemsResult.length &&
      getParItemsResult !== prevProps.getParItemsResult
    ) {
      this.setState({ parItems: getParItemsResult }, this.addParItems);
    }

    successToast(
      this.props,
      prevProps,
      'postSaveOrderResult',
      'Order saved successfully.',
      () => history.push(this.rootUrl)
    );

    errorToast(
      this.props,
      prevProps,
      'postSaveOrderErrors',
      clearPostSaveOrderResult,
    );

    //if our delivery date or site has changed, remove any items from our order
    //that are no longer in our site
    if (getAllSiteItemsResult !== prevProps.getAllSiteItemsResult) {
      const newOrderItems = { ...orderItems };
      let didRemove = false;

      for (let itemCode in newOrderItems) {
        if (newOrderItems.hasOwnProperty(itemCode)) {
          const siteItem = getAllSiteItemsResult.find(
            (item) => item[CreateEditOrderEnum.ItemCode] === itemCode
          );

          if (!siteItem) {
            delete newOrderItems[itemCode];
            didRemove = true;
          }
        }
      }

      if (didRemove) {
        change(CreateEditOrderEnum.TransactionDetail, newOrderItems);
      }
    }
  }

  componentWillUnmount() {
    this.props.clearAll();
  }

  getMainProps = () => {
    const {
      formValues: { [CreateEditOrderEnum.TransactionDetail]: orderItems },
      match: {
        params: { IsNewEditDup },
      },
      formPristine,
      getAllSiteItemsResult,
      getParItemsLoading,
      postSaveOrderLoading,
    } = this.props;

    const hasOrderItems = orderItems
      ? Boolean(Object.keys(orderItems).length)
      : false;

    const isDup = IsNewEditDup === CreateEditDupOrderEnum.DUP;

    const toolbox = {
      shown: true,
      backButtonProps: {
        shown: true,
        path: this.rootUrl,
        disabled: postSaveOrderLoading,
      },
      rightButtons: [
        {
          content: 'Reset',
          onClick: () => this.handleReset(),
          disabled: formPristine || postSaveOrderLoading,
        },
        {
          content: 'Add Par Items',
          onClick: this.handleAddParItems,
          loading: getParItemsLoading,
          disabled:
            !getAllSiteItemsResult.length ||
            getParItemsLoading ||
            hasOrderItems ||
            postSaveOrderLoading,
        },
        {
          content: 'Save Order',
          onClick: () => this.handleSubmit(),
          loading: postSaveOrderLoading,
          disabled:
            !isDup && (!hasOrderItems || postSaveOrderLoading || formPristine),
        },
      ],
    };

    return { toolbox };
  };

  handleAddParItems = () => {
    const {
      getParItems,
      formValues: {
        [CreateEditOrderEnum.SiteId]: siteId,
        [CreateEditOrderEnum.TransactionDT]: deliveryDate,
      },
    } = this.props;

    const { parItems } = this.state;

    //only fetch par items once and save a copy
    if (parItems) {
      this.addParItems();
    } else {
      getParItems(siteId, moment(deliveryDate).format(DATE_FORMAT_FOR_SERVER));
    }
  };

  addParItems = () => {
    const { parItems } = this.state;
    const { change } = this.props;

    const orderItems = {
      ...this.props.formValues[CreateEditOrderEnum.TransactionDetail],
    };

    for (let i = 0; i < parItems.length; i++) {
      const parItem = parItems[i];

      orderItems[parItem[ItemEnum.ItemCode]] = parItem;
    }

    change(CreateEditOrderEnum.TransactionDetail, orderItems);
  };

  clearParItems = () => {
    this.props.clearGetParItemsResult();
    this.setState({ parItems: null });
  };

  handleReset = (confirmed) => {
    if (!confirmed) {
      this.setState({ showResetModal: true });
      return;
    }

    //compare current values to initial values
    //if site id or delivery date are different, fetch or clear site items
    const {
      [CreateEditOrderEnum.SiteId]: siteId,
      [CreateEditOrderEnum.TransactionDT]: deliveryDate,
    } = this.props.formValues;

    const {
      [CreateEditOrderEnum.SiteId]: initialSiteId,
      [CreateEditOrderEnum.TransactionDT]: initialDeliveryDate,
    } = this.getInitialValues();

    if (siteId !== initialSiteId || deliveryDate !== initialDeliveryDate) {
      this.updateSiteItems(initialSiteId, initialDeliveryDate);
    }

    //reset the redux form to its initial values
    this.props.reset();
  };

  handleResetModalResponse = (response) => {
    if (response) {
      this.handleReset(true);
    }

    this.setState({ showResetModal: false });
  };

  handleSiteAndDeliveryDateChange = (newValue, oldValue, field, confirmed) => {
    //apply incoming value to formValues
    const newValues = { ...this.props.formValues };

    if (field) {
      newValues[field] = newValue;
    }

    const {
      [CreateEditOrderEnum.SiteId]: siteId,
      [CreateEditOrderEnum.TransactionDT]: deliveryDate,
      [CreateEditOrderEnum.TransactionDetail]: orderItems,
    } = newValues;

    if (!siteId || !deliveryDate) {
      return;
    }

    //show warning modal when trying to change site or date with items in order
    if (Object.keys(orderItems).length && !confirmed) {
      this.setState({
        showUnsavedModal: true,
        unsavedChange: {
          field,
          oldValue,
          newValue,
        },
      });

      return;
    }

    this.updateSiteItems(siteId, deliveryDate);
  };

  updateSiteItems = (siteId, deliveryDate) => {
    const { getAllSiteItems, clearGetAllSiteItemsResult } = this.props;

    //current order items will be handled in componentDidUpdate
    this.clearParItems();

    if (parseInt(siteId, 10) && parseInt(deliveryDate, 10)) {
      getAllSiteItems(
        siteId,
        moment(deliveryDate).format(DATE_FORMAT_FOR_SERVER)
      );
    } else {
      clearGetAllSiteItemsResult();
    }
  };

  handleUnsavedModalResponse = (response) => {
    const { field, oldValue, newValue } = this.state.unsavedChange;

    if (response) {
      //if we've confirmed that we want to make changes anyway, make changes
      this.handleSiteAndDeliveryDateChange(newValue, oldValue, field, true);
    } else {
      //otherwise change the form value back to what it was
      this.props.change(field, oldValue);
    }

    this.setState({ showUnsavedModal: false, unsavedChange: null });
  };

  handleSubmit = (confirmed) => {
    const {
      formValues,
      formErrors,
      match: {
        params: { IsNewEditDup, OrderId },
      },
      postSaveOrder,
      getOrderTypesResult,
    } = this.props;

    //if there are any errors, show a toast
    const errorStrings = Object.values(formErrors);

    if (errorStrings.length) {
      for (let i = 0; i < errorStrings.length; i++) {
        toastError(errorStrings[i]);
      }

      return;
    }

    //if our delivery date is > 30 days in the future, show a confirmation
    const deliveryDateMoment = moment(
      formValues[CreateEditOrderEnum.TransactionDT]
    );

    if (deliveryDateMoment.isAfter(moment().add(30, 'days')) && !confirmed) {
      this.setState({
        showDeliveryDateModal: true,
      });

      return;
    }

    const deliveryDate = deliveryDateMoment.format(DATE_FORMAT_FOR_SERVER);

    //serialize items from object to array
    const orderItems = Object.values(
      formValues[CreateEditOrderEnum.TransactionDetail]
    ).map((item) => {
      const { _added, ...orderItem } = item;

      return {
        Id: 0,
        ...orderItem,
        [CreateEditOrderEnum.PrimaryQty]: item[CreateEditOrderEnum.PriQty],
        [CreateEditOrderEnum.SecondaryQty]: item[CreateEditOrderEnum.SecQty],
      };
    });

    //if we want to duplicate an order, force the order id to be 0
    const orderId = IsNewEditDup === CreateEditDupOrderEnum.DUP ? 0 : OrderId;

    //build our payload
    const data = {
      [CreateEditOrderEnum.Id]: orderId,
      [CreateEditOrderEnum.SiteId]: formValues[CreateEditOrderEnum.SiteId],
      [CreateEditOrderEnum.TransactionDT]: deliveryDate,
      [CreateEditOrderEnum.Comments]: formValues[CreateEditOrderEnum.Comments],
      [CreateEditOrderEnum.Status]: StatusOptionEnum.OPEN,
      [CreateEditOrderEnum.Type]: TypeOptionEnum.ORDER,
      [CreateEditOrderEnum.TransactionDetail]: orderItems,
      [CreateEditOrderEnum.OrderTypeDetails]: makeOrderTypes(
        formValues[CreateEditOrderEnum.OrderTypeDetails],
        getOrderTypesResult
      ),
    };

    postSaveOrder(data);
  };

  handleDeliveryDateModalResponse = (response) => {
    if (response) {
      this.handleSubmit(true);
    }

    this.setState({ showDeliveryDateModal: false });
  };

  getBreadcrumbs = () => {
    const { IsNewEditDup } = this.props.match.params;

    const breadcrumbs = [
      {
        title: 'Manage Orders',
        link: this.rootUrl,
      },
      {
        title:
          IsNewEditDup === CreateEditDupOrderEnum.EDIT
            ? 'Edit Order'
            : 'Create New',
        link: '',
      },
    ];

    return breadcrumbs;
  };

  getInitialValues = () => {
    const {
      match: {
        params: { SiteId },
      },
      getOrderDetailsResult,
    } = this.props;

    //if we're editing an order, use those values first
    let existingValues = {};

    if (getOrderDetailsResult) {
      existingValues = pick(getOrderDetailsResult, [
        CreateEditOrderEnum.Id,
        CreateEditOrderEnum.SiteId,
        CreateEditOrderEnum.Comments,
        CreateEditOrderEnum.OrderTypeDetails,
      ]);

      existingValues[CreateEditOrderEnum.TransactionDT] = moment(
        getOrderDetailsResult[CreateEditOrderEnum.TransactionDT]
      ).format(DATE_FORMAT_FOR_UI);

      //deserialize items from array to object
      existingValues[
        CreateEditOrderEnum.TransactionDetail
      ] = getOrderDetailsResult[CreateEditOrderEnum.ItemList].reduce(
        (items, item) => ({ ...items, [item[ItemEnum.ItemCode]]: item }),
        {}
      );
    }

    //defaults
    const initialValues = {
      [CreateEditOrderEnum.SiteId]: SiteId,
      [CreateEditOrderEnum.TransactionDT]: moment().format(DATE_FORMAT_FOR_UI),
      [CreateEditOrderEnum.Comments]: '',
      [CreateEditOrderEnum.TransactionDetail]: {},
      [CreateEditOrderEnum.OrderTypeDetails]: [],
      ...existingValues,
    };

    return initialValues;
  };

  render() {
    const {
      getAllSiteItemsLoading,
      getAllSiteItemsResult,
      getOrderDetailsLoading,
      getOrderDetailsResult,
      getOrderTypesLoading,
      getOrderTypesResult,
      postSaveOrderLoading,
      addOrderType,
      match: {
        params: { IsNewEditDup },
      },
    } = this.props;

    const {
      showUnsavedModal,
      showDeliveryDateModal,
      showResetModal,
    } = this.state;

    const orderDate = moment(
      IsNewEditDup === CreateEditDupOrderEnum.EDIT && getOrderDetailsResult
        ? getOrderDetailsResult[CreateEditOrderEnum.OrderDate]
        : undefined
    ).format(DATE_FORMAT_FOR_UI);

    return getOrderDetailsLoading ? (
      <LoadingPage />
    ) : (
      <React.Fragment>
        <Filter title="" breadcrumbs={this.getBreadcrumbs()}>
          <div style={{ paddingTop: '10px', textAlign: 'right' }}>
            Order Date: {orderDate}
          </div>
        </Filter>

        <Main shouldNotUsePageWidth {...this.getMainProps()}>
          <CreateEditOrderForm
            initialValues={this.getInitialValues()}
            onSiteAndDeliveryDateChange={this.handleSiteAndDeliveryDateChange}
            orderItemsTableName={
              TableNamesEnum.MANAGEPROCESSORDER_CREATEEDIT_ORDERITEMS_TABLE
            }
            orderItemsGridName={
              GridEnum.INV_ManageProcessOrder_CreateEdit_OrderItems_View
            }
            siteItemsTableName={
              TableNamesEnum.MANAGEPROCESSORDER_CREATEEDIT_SITEITEMS_TABLE
            }
            siteItemsGridName={
              GridEnum.INV_ManageProcessOrder_CreateEdit_SiteItems_View
            }
            siteItems={getAllSiteItemsResult}
            orderTypes={getOrderTypesResult}
            orderTypesLoading={getOrderTypesLoading}
            onOrderTypeAdd={addOrderType}
            disabled={
              get(getOrderDetailsResult, CreateEditOrderEnum.Status) ===
              ManageProcessOrderStatusEnum.CLOSED
            }
            loading={getAllSiteItemsLoading || postSaveOrderLoading}
          />
        </Main>

        <YesNoDialog
          showDialog={showUnsavedModal}
          title="Unsaved changes detected"
          question={UNSAVED_MESSAGE}
          yesCallback={() => this.handleUnsavedModalResponse(true)}
          noCallback={() => this.handleUnsavedModalResponse(false)}
        />

        <YesNoDialog
          showDialog={showDeliveryDateModal}
          title="Inventory"
          question="Delivery date is more than 30 days out, are you sure?"
          yesCallback={() => this.handleDeliveryDateModalResponse(true)}
          noCallback={() => this.handleDeliveryDateModalResponse(false)}
        />

        <YesNoDialog
          showDialog={showResetModal}
          title="Inventory"
          question="All of your unsaved changes will be lost, are you sure?"
          yesCallback={() => this.handleResetModalResponse(true)}
          noCallback={() => this.handleResetModalResponse(false)}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    formValues: getFormValues(CREATE_EDIT_ORDER_FORM)(state) || {},
    formPristine: isPristine(CREATE_EDIT_ORDER_FORM)(state),
    formErrors: getFormSyncErrors(CREATE_EDIT_ORDER_FORM)(state),

    getAllSiteItemsLoading: CreateEditOrderSelectors.getAllSiteItemsLoading(
      state
    ),
    getAllSiteItemsResult: CreateEditOrderSelectors.getAllSiteItemsResult(
      state
    ),

    getParItemsLoading: CreateEditOrderSelectors.getParItemsLoading(state),
    getParItemsResult: CreateEditOrderSelectors.getParItemsResult(state),

    postSaveOrderLoading: CreateEditOrderSelectors.postSaveOrderLoading(state),
    postSaveOrderResult: CreateEditOrderSelectors.postSaveOrderResult(state),

    getOrderDetailsLoading: CreateEditOrderSelectors.getOrderDetailsLoading(
      state
    ),
    getOrderDetailsResult: CreateEditOrderSelectors.getOrderDetailsResult(
      state
    ),

    getOrderTypesLoading: CreateEditOrderSelectors.getOrderTypesLoading(state),
    getOrderTypesResult: CreateEditOrderSelectors.getOrderTypesResult(state),
  };
};

const mapDispatchToProps = {
  change: (...args) => change(CREATE_EDIT_ORDER_FORM, ...args),
  reset: () => reset(CREATE_EDIT_ORDER_FORM),

  getAllSiteItems: CreateEditOrderActions.getAllSiteItems,
  clearGetAllSiteItemsResult: CreateEditOrderActions.clearGetAllSiteItemsResult,
  getParItems: CreateEditOrderActions.getParItems,
  clearGetParItemsResult: CreateEditOrderActions.clearGetParItemsResult,
  postSaveOrder: CreateEditOrderActions.postSaveOrder,
  clearPostSaveOrderResult: CreateEditOrderActions.clearPostSaveOrderResult,
  getOrderDetails: CreateEditOrderActions.getOrderDetails,
  getOrderTypes: CreateEditOrderActions.getOrderTypes,
  addOrderType: CreateEditOrderActions.addOrderType,

  clearAll: CreateEditOrderActions.clearAll,
};

export const CreateEditOrder = connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateEditOrderContainer);



// WEBPACK FOOTER //
// ./src/pages/inventory/manageprocessorder/containers/subpages/CreateEditOrder.js